// models
import { CompactTabItem } from '../components/nav/CompactTabMenu/CompactTab';

import { FormulaType } from 'constants/formulaType';

import { ComponentTypeStatus } from './component-types-model';

import {
    ApprovedFormulaState,
    FormulaState,
    FormulaStatus,
} from './formula.model';
import { LabelMessageStatus, LabelSendingCriteria } from './label.model';
import { UnlabelledVariant } from './variant.model';

export interface PersonalizationMapFormula {
    formula_id: number;
    formula_name: string;
    status: FormulaStatus | ApprovedFormulaState;
    count: number;
}

export interface FormulaStateCount {
    status: LabelMessageStatus;
    count: number;
}

export type FormulaStateCounts = {
    [message_status in LabelMessageStatus]?: FormulaStateCount;
};

export interface LabelCountDetail {
    label_name: string;
    count: number;
    formulas: PersonalizationMapFormula[];
    formula_states: FormulaStateCounts;
}
export interface ComparedTypeLabelsMap {
    [label_id: number]: LabelCountDetail;
}

export interface LabelDetails {
    label_name: string;
    counts: ComparedTypeLabelsMap;
}

export interface PersonalizationOpportunityMap {
    [offering_label_id: number]: LabelDetails;
}

export interface ContentCountDetails {
    label_x_count: number;
    label_y_count: number;
    co_occurrence_count: number;
    formula_label_x: boolean;
    formula_label_y: boolean;
}

export interface ContentFormulaDetails extends ContentCountDetails {
    formula_id: number;
    formula_name: string;
    formula_state: FormulaStatus | ApprovedFormulaState;
}

export interface ContentFormulaMap {
    [formula_id: number]: ContentFormulaDetails;
}
export interface YAxisMap extends ContentCountDetails {
    label_name: string;
    label_id: number;
    formulas: ContentFormulaMap;
}

export interface XAxisMap {
    label_name: string;
    label_id: number;
    counts: Record<number, YAxisMap>;
}

export interface ContentCoverageMap {
    [x_label_id: number]: XAxisMap;
}

export interface ContentCoverageFilters {
    formula_states?: LabelMessageStatus[];
    channels?: FormulaType[];
    audience_ids?: (string | null)[];
    formula_ids?: number[];
    language_ids?: string[];
    sending_criteria?: LabelSendingCriteria[];
    tag_ids?: string[];
}
export interface PersonalizationMapFilterFormikValues {
    x_axis_variant_type?: string;
    x_axis_labels: number[];
    y_axis_variant_type?: string;
    y_axis_labels: number[];
    message_status?: LabelMessageStatus[];
    sort_option: SortByOptions;
}

export enum PersonalizationTab {
    OVERVIEW = 'overview',
    OPPORTUNITY_MAP = 'opportunity_map',
    CONTENT_COVERAGE = 'content_coverage',
    COMPONENTS_AND_LABELS = 'components_and_labels',
    UNLABELED_ALTERNATES = 'unlabelled_alternates',
    UNLABELED_MESSAGES = 'unlabelled_messages',
}

export enum SortByOptions {
    FILLED_FIRST = 'filled_first',
    RANDOM = 'random',
    EMPTY_FIRST = 'empty_first',
}
export enum LabelManagementSubTab {
    LABEL_PERFORMANCE = 'label_performance',
}
export enum UnlabeledContentTab {
    UNLABELED_ALTERNATES = 'unlabeled-alternates',
    UNLABELED_MESSAGES = 'unlabeled-messages',
}

const UnLabelledAlternatesTab: CompactTabItem = {
    isPrimary: true,
    key: PersonalizationTab.UNLABELED_ALTERNATES,
    name: 'Unlabeled alternates',
};

const UnLabelledMessagesTab: CompactTabItem = {
    isPrimary: true,
    key: PersonalizationTab.UNLABELED_MESSAGES,
    name: 'Unlabeled messages',
};

const LabelPerformanceTab: CompactTabItem = {
    isPrimary: true,
    key: LabelManagementSubTab.LABEL_PERFORMANCE,
    name: 'Label performance',
};

const ComponentsAndLabelTab: CompactTabItem = {
    isPrimary: true,
    key: PersonalizationTab.COMPONENTS_AND_LABELS,
    name: 'Components and labels',
};

const UnlabeledAlternatesTab: CompactTabItem = {
    name: 'Unlabeled alternates',
    key: UnlabeledContentTab.UNLABELED_ALTERNATES,
    isPrimary: true,
};
const UnlabeledMessagesTab: CompactTabItem = {
    name: 'Unlabeled messages',
    key: UnlabeledContentTab.UNLABELED_MESSAGES,
    isPrimary: true,
};
const ContentCoverage: CompactTabItem = {
    name: 'Content coverage',
    key: PersonalizationTab.CONTENT_COVERAGE,
    isPrimary: true,
    tooltip:
        'Easily compare your offerings and value propositions to ensure you have variants that talk about each of your offerings in a way that appeals to each of your users.',
};

export const LabelManagementManagerTabs: CompactTabItem[] = [
    ComponentsAndLabelTab,
    LabelPerformanceTab,
    ContentCoverage,
    UnLabelledAlternatesTab,
    UnLabelledMessagesTab,
];

export const UnlabeledContentTabs: CompactTabItem[] = [
    UnlabeledAlternatesTab,
    UnlabeledMessagesTab,
];

export const ComponentTypeStatusTabs: CompactTabItem[] = [
    {
        name: ComponentTypeStatus.Live,
        key: ComponentTypeStatus.Live,
        isPrimary: true,
    },
    {
        name: 'All',
        key: 'All',
        isPrimary: true,
    },
    {
        name: ComponentTypeStatus.Archived,
        key: ComponentTypeStatus.Archived,
        isPrimary: true,
    },
];

export const DEFAULT_MESSAGE_STATUS_FOR_QUERY: LabelMessageStatus[] = [
    LabelMessageStatus.Live,
];

export interface UnlabeledVariantsTabs {
    tabs: CompactTabItem[];
    tabsData: Record<string, UnlabelledVariant[]>;
}

export const DEFAULT_SORT_OPTION = SortByOptions.RANDOM;

export interface FormulaCoverageMap {
    formula_name: string;
    formula_id: number;
    message_type: FormulaType;
    surface_id?: string;
    audience_id?: string;
    audience_name?: string;
    formula_state: FormulaState;
    users: number;
    labels: {
        label_count: number;
        label_id: number;
        label_name: string;
        is_formula_label?: boolean;
    }[];
}

export interface HeroRecommendation {
    pair_score: number;
    label_x: {
        id: string;
        name: string;
        variant_type: string;
        component_type_id: string;
    };
    label_y: {
        id: string;
        name: string;
        variant_type: string;
        component_type_id: string;
    };
}

export interface ContentCoverageRecommendation {
    label_id_x: string;
    label_id_y: string;
    label_x_name: string;
    label_y_name: string;
    rank: number;
    score: number;
}
